const HERO_IMAGE =
  'https://images.unsplash.com/photo-1504280390367-361c6d9f38f4?auto=format&fit=crop&w=1600&q=80';

/** Featured guide: family adventure destinations in Columbus, OH and nearby for kids ages 10–13 */
export const COLUMBUS_FAMILY_ADVENTURE_TWEENS_POST = {
  id: 'featured-columbus-family-adventure-tweens',
  slug: 'columbus-family-adventure-kids-10-13-guide',
  title: 'Best Family Adventures in Columbus, Ohio for Kids Ages 10–13',
  excerpt:
    'Zip lines, ninja courses, cave tours, kayaking, and day trips within 90 minutes of Columbus—curated for tweens who want real adventure, not just playgrounds.',
  publishedAt: '2026-07-27T18:00:00.000Z',
  coverImageUrl: HERO_IMAGE,
  authorName: 'Daily Sports Life',
  clubName: 'Ohio Family Outdoors',
  blogCategory: 'club-news',
  audience: 'Ages 10–13',
  featured: true,
  component: 'WellnessGuideBlogArticle',
  theme: 'diet',
  sections: [
    {
      type: 'stats',
      ariaLabel: 'Guide overview',
      items: [
        { value: '25+', label: 'Adventure spots listed' },
        { value: 'Ages 10–13', label: 'Tween-focused picks' },
        { value: '90 min', label: 'Max drive from Columbus' },
        { value: 'All seasons', label: 'Indoor & outdoor' },
      ],
    },
    {
      type: 'panel',
      title: 'How to use this guide',
      paragraphs: [
        'Kids ages 10–13 are in the sweet spot for real adventure: tall enough for zip lines and climbing walls, strong enough for half-day hikes and paddling, and curious enough to enjoy science museums and escape rooms without feeling like they are on a “little kid” trip.',
        'This list focuses on Columbus and nearby day-trip territory—roughly within 90 minutes of downtown. We grouped spots by adventure type so you can match energy level, weather, and budget. Most picks work year-round with an indoor backup; summer adds water parks and river floats.',
        'Book ahead for zip lines, escape rooms, and weekend canoe trips. Pair geographically close stops (Easton + Scioto Audubon, or Polaris + Olentangy Caverns) to avoid criss-crossing the metro.',
      ],
      chips: ['Columbus metro', 'Ages 10–13', 'Family adventure', 'Day trips'],
    },
    {
      type: 'table',
      title: 'Aerial & treetop adventures — zip lines and climbing parks',
      intro:
        'These are the headline picks for active tweens. Height and weight minimums apply—check each venue before booking.',
      headers: ['Place', 'Location / drive', 'Best for ages', 'Why tweens love it'],
      rows: [
        [
          'ZipZone Outdoor Adventures',
          'North Columbus (~20 min from downtown)',
          'Ages 7+ (tweens thrive on expert courses)',
          'Largest forest climbing park in central Ohio—5 aerial courses, 60+ obstacles, and guided canopy zip tours with sky bridges. Self-guided Adventure Park lets each kid pick difficulty like a ski mountain.',
        ],
        [
          'ZipZone Canopy Tour',
          'Same location as Adventure Park',
          'Ages 7+',
          '2-hour guided tour: 5 zip lines and 4 suspension bridges through 20 acres of forest. Night Flight tours (summer/fall weekends) add extra thrill for brave 10–13-year-olds.',
        ],
        [
          'The Wilds — Zipline Safari',
          'Cumberland (~75 min east)',
          'Ages 10+',
          'Soar over open-range animal habitats—giraffes, rhinos, and takins below. Combine with a Wilds safari tour for a full-day adventure east of Columbus.',
        ],
        [
          'Hocking Hills Canopy Tours',
          'Rockbridge (~55 min southeast)',
          'Ages 10+ (supervised younger on some tours)',
          'SuperZip and original canopy tours through Hocking Hills forest. Pair with a short gorge hike the same day for a classic Ohio adventure weekend.',
        ],
        [
          'Go Ape Zipline & Adventure Park',
          'Mill Creek Metro Park, Youngstown area (~2 hr northeast)',
          'Ages 10+',
          'Treetop ropes, Tarzan swings, and zip lines in a scenic metro park. Worth the longer drive for a birthday or long weekend.',
        ],
      ],
    },
    {
      type: 'panel',
      title: 'Pick #1 — ZipZone Adventure Park (best all-around aerial day)',
      orderedList: [
        'Book a 2-hour Adventure Park session online—weekday mornings have the shortest wait.',
        'Arrive 30–40 minutes early for harness fitting and safety briefing.',
        'Start on a green (beginner) course together, then let confident tweens try blue and black routes.',
        'Pack gloves (or buy on-site), closed-toe shoes, and water—climbing is a workout.',
        'Celebrate with lunch at the on-site picnic area or head to Highbanks Metro Park (15 min north) for a cooldown hike.',
      ],
      callout:
        'Address: 7925 N High St, Columbus, OH 43235. Junior pricing applies ages 7–11; most 12–13-year-olds pay general admission.',
    },
    {
      type: 'table',
      title: 'Ninja, parkour & indoor obstacle challenges',
      headers: ['Place', 'Location / drive', 'Best for ages', 'Why tweens love it'],
      rows: [
        [
          'Ninja Citi Adventure Park',
          'North Columbus (~20 min)',
          'Ages 7+ (46" min for parkour arena)',
          '30,000 sq ft with 12 ninja courses, warped walls up to 14 ft, parkour arena, rock wall, trampolines, and arcade. Perfect rainy-day energy burner.',
        ],
        [
          'DEFY Columbus (trampoline park)',
          'Multiple Columbus-area locations',
          'Ages 6+',
          'Wall trampolines, foam pits, ninja course, and dodgeball—good for mixed-age siblings when one kid needs simpler obstacles.',
        ],
        [
          'Vertical Adventures',
          'Clintonville / north Columbus',
          'Ages 6+ with adult; tweens climb independently with waiver',
          'Indoor rock climbing and bouldering. Intro classes teach belay basics; tweens progress fast and love the problem-solving.',
        ],
        [
          'Kinetic Climbing + Fitness',
          'Grandview / near OSU',
          'Ages 6+',
          'Bouldering-focused gym with youth programs. Less gear, more movement—appeals to kids who want to climb without ropes class first.',
        ],
        [
          'Scene75 Entertainment Center',
          'Brice / east side (~20 min)',
          'Ages 8+',
          'Go-karts, laser tag, arcade, mini golf, and indoor coasters—full evening adventure when weather shuts down outdoor plans.',
        ],
      ],
    },
    {
      type: 'table',
      title: 'Water adventures — rivers, lakes, and splash parks',
      intro:
        'Summer is prime paddling season. Always wear life jackets on the water; tweens still need adult supervision on moving rivers.',
      headers: ['Place', 'Location / drive', 'Best for ages', 'Why tweens love it'],
      rows: [
        [
          'Scioto Audubon Metro Park — climbing wall + river',
          'Downtown riverfront (~10 min)',
          'Ages 10+ on 35-ft free climbing wall (with harness)',
          'North America’s tallest outdoor free climbing wall, plus sand volleyball, bouldering area, and Scioto Mile access. Free entry; climbing requires gear rental or your own.',
        ],
        [
          'Trapper John’s Canoe Livery',
          'Big Darby Creek, Plain City (~25 min west)',
          'Ages 5+ on calm stretches',
          'Canoe and kayak rentals on scenic Big Darby Creek—gentle enough for first-timers, wild enough to feel like a real expedition.',
        ],
        [
          'RiversEdge Outfitters',
          'Little Miami River, Spring Valley (~50 min southwest)',
          'Ages 3+ (tweens love solo kayaks)',
          'Hourly departures on one of Ohio’s cleanest rivers. Half-day and full-day float options; rafts work well for whole families.',
        ],
        [
          'Mad River Adventures',
          'Mohican River, Loudonville (~75 min)',
          'Ages 6+',
          'Canoe, kayak, and raft trips through Mohican gorge country—the classic Ohio summer float tweens remember for years.',
        ],
        [
          'Alum Creek State Park',
          'Lewis Center / Delaware (~25 min north)',
          'Ages 3–13',
          'Swimming beach, boat rentals, and long shoreline trails. Easy lake day when kids want water without a river current.',
        ],
        [
          'Zoombezi Bay',
          'Adjacent to Columbus Zoo (~25 min north)',
          'Ages 8+ on bigger slides',
          'Seasonal water park with high-thrill slides, wave pool, and lazy river. Pair with a morning zoo visit for a full adventure day.',
        ],
        [
          'O\'Shaughnessy Reservoir paddling',
          'Powell / Dublin area (~20 min)',
          'Ages 8+',
          'Kayak and SUP rentals in summer; calm water ideal for learning paddle skills close to northwest suburbs.',
        ],
      ],
    },
    {
      type: 'table',
      title: 'Caves, gorges & outdoor exploration',
      headers: ['Place', 'Location / drive', 'Best for ages', 'Why tweens love it'],
      rows: [
        [
          'Olentangy Caverns',
          'Delaware (~30 min north)',
          'Ages 4+ (tweens enjoy gem mining & maze most)',
          'Self-guided cave tour plus gem mining, treasure maze, mini golf, petting zoo, and Bigfoot nature trail. Rain-or-shine outdoor adventure park.',
        ],
        [
          'Hocking Hills State Park',
          'Logan (~55 min southeast)',
          'Ages 8+ on longer loops',
          'Old Man’s Cave, Cedar Falls, Ash Cave, and Cantwell Cliffs—dramatic gorges tweens rate as “actually cool.” Plan 4–6 hours for two trail segments.',
        ],
        [
          'John Bryan State Park & Clifton Gorge',
          'Yellow Springs (~50 min southwest)',
          'Ages 8+ (cliff edges—stay on trails)',
          'Limestone gorge carved by the Little Miami River. Geology-curious tweens love the rock layers; Clifton Mill is a short add-on.',
        ],
        [
          'Highbanks Metro Park',
          'Lewis Center (~20 min north)',
          'Ages 5–13',
          '100-ft shale bluffs over the Olentangy, eagle nest overlook, and longer ridge trails. Free and less crowded than Hocking Hills on summer Saturdays.',
        ],
        [
          'Battelle Darby Creek Metro Park',
          'Galloway (~25 min west)',
          'Ages 5–13',
          'Live bison herd visible from trails, Big Darby Creek access, and a nature center. Assign tweens “wildlife spotter” duty for the bison pastures.',
        ],
        [
          'Indian Run Falls',
          'Dublin (~20 min northwest)',
          'Ages 6+',
          'Short hike to a waterfall in a suburban park—quick adventure when you only have 90 minutes.',
        ],
      ],
    },
    {
      type: 'table',
      title: 'Science, wildlife & mind-bending indoor adventures',
      headers: ['Place', 'Location / drive', 'Best for ages', 'Why tweens love it'],
      rows: [
        [
          'COSI (Center of Science and Industry)',
          'Downtown riverfront',
          'Ages 7–13',
          'Hands-on science museum with a dedicated Tween Lounge (STEAM projects, motion games, LEGO). Planetarium, dinosaur gallery, and ocean exhibits fill a rainy day.',
        ],
        [
          'Columbus Zoo and Aquarium',
          'Powell / northwest side',
          'Ages 5–13',
          'Polar Frontier, Heart of Africa, and Adventure Cove manatees. Tweens engage with conservation stories and longer walking routes across 580 acres.',
        ],
        [
          'The Escape Game — Easton',
          'Easton Town Center (~15 min northeast)',
          'Ages 13+ recommended; younger with adults',
          'Premium 60-minute escape rooms (Gold Rush, Prison Break, The Heist). Book a family-friendly room and let tweens lead puzzle-solving.',
        ],
        [
          'All In Adventures — Polaris',
          'Polaris Fashion Place (~20 min north)',
          'Ages 6+',
          'Multiple themed escape rooms at the mall—easier logistics than downtown and good for first-timer groups.',
        ],
        [
          'Franklin Park Conservatory',
          'East side (~10 min)',
          'Ages 5–13',
          'Biomes, glass art, and outdoor children’s garden. Pair with a COSI morning for a science-and-nature double header.',
        ],
        [
          'Ohio History Center & Ohio Village',
          'Near Ohio State Fairgrounds (~15 min)',
          'Ages 8+',
          'Interactive history exhibits and seasonal village events. Underrated when tweens are museum-curious but want something different from COSI.',
        ],
      ],
    },
    {
      type: 'table',
      title: 'Day trips & weekend getaways (within ~90 minutes)',
      intro:
        'When Columbus locals want a bigger adventure, these destinations deliver without an overnight stay—though an extra day is worth it at Mohican and Hocking Hills.',
      headers: ['Destination', 'Drive from Columbus', 'Best for ages', 'Adventure highlight'],
      rows: [
        [
          'Mohican State Park',
          '~75 min northeast',
          'Ages 8+',
          'Canoe the Mohican River, hike Clear Fork Gorge overlook, and explore covered-bridge country. Ohio’s classic outdoor weekend.',
        ],
        [
          'The Wilds',
          '~75 min east',
          'Ages 10+ for zipline',
          'Open-range safari by bus or truck, zipline over wildlife, and conservation education. One of the most unique adventures in the Midwest.',
        ],
        [
          'Yellow Springs + Glen Helen',
          '~50 min southwest',
          'Ages 8+',
          'Artsy village, Glen Helen Nature Preserve trails, and Young’s Jersey Dairy for ice cream. Low-key adventure with great food stops.',
        ],
        [
          'Cedar Point',
          '~2.5 hr north (long day trip)',
          'Ages 10+ on major coasters',
          'Roller-coaster capital on Lake Erie. Start early, stay until evening—tweens who love thrills will plan the ride order themselves.',
        ],
        [
          'Put-in-Bay (South Bass Island)',
          '~2.5 hr to ferry (Port Clinton)',
          'Ages 8+',
          'Ferry ride, Perry’s Victory monument climb, kayak rentals, and island biking. Feels like a vacation without flying.',
        ],
        [
          'Cuyahoga Valley National Park',
          '~2 hr northeast',
          'Ages 8+',
          'Towpath Trail biking, Brandywine Falls, Ledges Trail rock formations. National-park scenery on a Columbus long weekend.',
        ],
      ],
    },
    {
      type: 'panel',
      title: 'Top 10 must-do adventures (if you only pick ten)',
      orderedList: [
        'ZipZone Adventure Park — treetop obstacles and zip lines close to home.',
        'Ninja Citi — warped walls and parkour when rain cancels outdoor plans.',
        'Hocking Hills — Old Man’s Cave loop for Ohio’s best gorge scenery.',
        'COSI — Tween Lounge and planetarium on a cold or rainy day.',
        'Columbus Zoo + Zoombezi Bay — wildlife morning, water slides afternoon (summer).',
        'Scioto Audubon — free climbing wall with downtown skyline views.',
        'Olentangy Caverns — cave tour plus gem mining and treasure maze.',
        'Mohican River canoe float — full summer day on the water.',
        'The Wilds zipline safari — animals below, adrenaline above (ages 10+).',
        'Trapper John’s or RiversEdge paddling — first river trip tweens won’t forget.',
      ],
    },
    {
      type: 'table',
      title: 'Four ready-made adventure days from Columbus',
      headers: ['Theme', 'Morning', 'Afternoon', 'Best season'],
      rows: [
        [
          'Treetop thrill day',
          'ZipZone Adventure Park (2-hour climb)',
          'Highbanks bluff hike + picnic',
          'Spring through fall',
        ],
        [
          'Downtown discovery',
          'COSI exhibits + planetarium show',
          'Scioto Audubon climbing wall + Scioto Mile walk',
          'Year-round',
        ],
        [
          'Cave & creek day',
          'Olentangy Caverns tour + gem mining',
          'Trapper John’s canoe on Big Darby',
          'Summer',
        ],
        [
          'Zoo & splash',
          'Columbus Zoo (Polar Frontier + Africa)',
          'Zoombezi Bay slides and wave pool',
          'May–September',
        ],
        [
          'Gorge expedition',
          'Hocking Hills — Old Man’s Cave',
          'Cedar Falls + Ash Cave short loop',
          'Spring and fall (avoid summer crowds if possible)',
        ],
        [
          'Ninja & escape',
          'Ninja Citi obstacle courses',
          'All In Adventures escape room at Polaris',
          'Rainy day / winter',
        ],
      ],
    },
    {
      type: 'panel',
      title: 'What makes a spot great for ages 10–13',
      paragraphs: [
        'Tweens want challenge, autonomy, and bragging rights—not just playgrounds. The best picks let them pick difficulty (ZipZone color-coded courses), lead the group (escape-room clue hunting), or learn a real skill (kayak steering, climbing belay).',
        'They also tire of being treated like little kids. Choose venues with teen-appropriate thrills (warped walls, black-diamond aerial routes) and skip activities capped at age 8 unless a younger sibling is along.',
      ],
      list: [
        'Assign roles: navigator on hikes, photographer at the zoo, “safety officer” on paddle trips.',
        'Let them help plan one stop per day—it cuts complaints and builds ownership.',
        'Build in food rewards: Jeni’s Splendid Ice Creams, Young’s Jersey Dairy, or Easton food hall after a long adventure.',
        'Pack snacks and 2+ liters of water per kid—tween appetites spike after climbing and hiking.',
      ],
    },
    {
      type: 'panel',
      title: 'Practical tips for Columbus families',
      list: [
        'Columbus sprawls—cluster stops by area: northwest (zoo, Zoombezi, ZipZone), downtown (COSI, Scioto Audubon), northeast (Easton escape rooms), north (Olentangy Caverns, Highbanks).',
        'Book zip lines, escape rooms, and summer canoe trips 1–2 weeks ahead for weekends.',
        'Metro parks and Scioto Audubon are free; state parks have no gate fee. Budget for rentals, tickets, and parking at zoos and attractions.',
        'Summer: start outdoor activities by 9 AM; save COSI, ninja parks, and escape rooms for afternoon heat.',
        'Winter: ZipZone, Vertical Adventures, Ninja Citi, COSI, and Scene75 keep adventure going when trails are icy.',
        'Check height/weight rules before promising a zipline—The Wilds requires age 10+; some Hocking tours have similar limits.',
        'Download offline maps for Hocking Hills and Mohican—cell service is spotty in gorges.',
      ],
    },
    {
      type: 'panel',
      title: 'Sample three-day Columbus adventure weekend',
      orderedList: [
        'Day 1: Morning ZipZone Adventure Park → lunch → afternoon Scioto Audubon climbing wall and river walk.',
        'Day 2: Full day Hocking Hills (Old Man’s Cave + Cedar Falls) with packed lunch; pizza in Logan on the drive home.',
        'Day 3: Columbus Zoo morning → Zoombezi Bay afternoon (summer) or COSI + escape room at Easton (off-season).',
      ],
      callout:
        'Need calmer nature time with younger siblings? See our Ohio summer nature guide and Polaris picnic day plans for mixed-age families.',
    },
  ],
  relatedPostsTitle: 'More Ohio family guides',
  relatedPosts: [
    {
      slug: 'ohio-beginner-hiking-guide',
      title: 'Ohio Beginner Hiking Guide: Easy First Trails Across the State',
      blurb: 'easy first trails before the bigger gorge days.',
    },
    {
      slug: 'ohio-summer-nature-kids-family-guide',
      title: 'Ohio Summer Nature Guide for Families (Ages 3–13)',
      blurb: 'waterfalls, beaches, and state parks across Ohio.',
    },
    {
      slug: 'half-day-picnic-near-polaris-ohio-kids-guide',
      title: 'Nature Picnic Day Near Polaris, Ohio',
      blurb: 'morning and afternoon park plans north of Columbus.',
    },
    {
      slug: 'open-soccer-fields-powell-dublin-lewis-center-delaware-ohio',
      title: 'Open Soccer Fields Near Powell, Dublin, Lewis Center & Delaware',
      blurb: 'kick around after a hike or paddle day.',
    },
  ],
  referenceLinks: [
    {
      label: 'ZipZone Outdoor Adventures — reservations',
      url: 'https://zipzonetours.com/ohio-zipzone-reservations/',
    },
    {
      label: 'Ninja Citi Adventure Park — Columbus',
      url: 'https://www.ninjaciticolumbusoh.com/',
    },
    {
      label: 'Olentangy Caverns',
      url: 'https://www.olentangycaverns.com/',
    },
    {
      label: 'COSI — plan your visit',
      url: 'https://cosi.org/visit',
    },
    {
      label: 'Columbus Zoo and Aquarium',
      url: 'https://www.columbuszoo.org/',
    },
    {
      label: 'Columbus Metro Parks',
      url: 'https://www.metroparks.net/',
    },
    {
      label: 'Scioto Audubon Metro Park',
      url: 'https://www.metroparks.net/parks-and-trails/scioto-audubon/',
    },
    {
      label: 'Hocking Hills State Park (ODNR)',
      url: 'https://ohiodnr.gov/go-and-do/plan-a-visit/find-a-property/hocking-hills-state-park',
    },
    {
      label: 'The Wilds — zipline safari',
      url: 'https://thewilds.columbuszoo.org/home/adventure/zipline-safari',
    },
    {
      label: 'RiversEdge Outfitters — Little Miami River',
      url: 'https://riversedgeoutfitters.com/columbus/',
    },
    {
      label: 'Experience Columbus — family travel',
      url: 'https://www.experiencecolumbus.com/things-to-do/family-fun/',
    },
  ],
};
